/**
 * 🛰️ ScreenshotCabinet
 * 規範：docs/blueprints/SOVEREIGN_ARCH_GUIDELINE.md §3
 *  - 禁止改活地圖尺寸；一律用隱藏 ghost（z-index:-9999）
 *  - 「公車報表」：有分時圈 → 雙圖（路線地圖＋190×95 mm 清單卡，非整頁）
 *  - 「分時人口報表」：有分時圈 → 雙圖（動態尺寸地圖＋固定比例人口表）
 *  - 「生活機能圖 (全機能)」：固定 17.9×12.1 cm；**直接截活地圖已載入圖磚／圖示**（禁重砌 ghost 重抓）；
 *    有分時圈：fitBounds → **依圈裁成紙本長寬比**（以圈為基準，禁整屏 letterbox）→ 還原視圖；
 *    支援 POI、YouBike、停車場等全機能圖層；不帶圖例
 */

import { SovereignCabinet } from '../../core/cabinet-base.js';
import { SovereignEvents } from '../../core/cabinet-registry.js';
import {
    SCREENSHOT_FORMATS,
    SCREENSHOT_PRINT_SCALE,
    SCREENSHOT_LAYOUT_DPI,
    formatPixelSize,
    computeIsoDynamicViewport,
    captureGhostMapFromLive,
    captureLiveMapPrint,
    downloadCanvasPng,
    LIGHT_TRANSPORT_TILE_URL,
    LIGHT_CARTO_TILE_URL
} from '../../utils/sovereign-map-capture.js';

/** fetch 失敗時仍要看得見按鈕（避免展開後空白） */
const FALLBACK_HTML = `<div class="cabinet-panel">
  <div class="header"><h3>📷 截圖工作箱</h3></div>
  <div class="content">
    <div style="margin-bottom:12px;">
      <input type="text" id="screenshotTitle" placeholder="🖼️ 輸入截圖標題 (浮水印)..." class="sovereign-input" style="width:100%;" />
    </div>
    <div class="grid-layout">
      <button class="action-btn" id="btnModeReport" title="有分時圈時：一次下載公車路線圖＋分鐘清單兩張 PNG">🚌 公車報表</button>
      <button class="action-btn" id="btnModeCanva" title="有分時圈時：動態尺寸地圖（淺色交通底圖）＋固定比例人口表">👥 分時人口報表</button>
    </div>
    <button class="action-btn" id="btnModeGlobal" title="固定 17.9×12.1 cm；有分時圈時以圈為基準裁切取景；支援 POI、YouBike、停車場等全機能圖層">📍 生活機能圖 (全機能)</button>
    <div id="status-text" class="status-text">就緒 · ghost 300 dpi 印刷，不改活地圖</div>
  </div>
</div>`;

const FALLBACK_CSS = `.cabinet-panel{width:100%;max-width:220px;background:rgba(20,25,35,0.85);backdrop-filter:blur(10px);border:1px solid rgba(200,200,200,0.3);border-radius:8px;color:#fff;font-family:sans-serif;margin-bottom:10px;pointer-events:auto;box-sizing:border-box}
.header{padding:10px 15px;background:linear-gradient(90deg,rgba(200,200,200,0.3) 0%,rgba(20,25,35,1) 100%);border-radius:8px 8px 0 0}
.header h3{margin:0;font-size:15px;color:#ddd}
.content{padding:15px;display:flex;flex-direction:column;gap:10px}
.grid-layout{display:flex;flex-wrap:wrap;gap:8px}
.action-btn{background:rgba(200,200,200,0.2);border:1px solid #ddd;color:#fff;padding:8px;border-radius:4px;cursor:pointer;font-size:13px}
.action-btn:hover{background:rgba(200,200,200,0.4)}
.status-text{font-size:12px;color:#aaa;text-align:center}
.sovereign-input{background:rgba(0,0,0,0.35);border:1px solid #555;color:#fff;padding:6px 8px;border-radius:4px;box-sizing:border-box}`;

class ScreenshotCabinet extends SovereignCabinet {
    constructor() {
        super();
        this.activeIsoPolygon = null;
        /** @type {object[]} 全部分鐘圈（由大到小）；取景／裁點用外圈並集 */
        this.activeIsoPolygons = [];
        this._exporting = false;
        // 先灌後備，避免 fetch 未完成／失敗時展開一片空白
        this.setTemplate(FALLBACK_HTML, FALLBACK_CSS);
        this.loadAssets('/js/cabinets/screenshot');
    }

    onMount() {
        const bind = (id, mode) => {
            const el = this.shadowRoot?.getElementById(id);
            if (!el || el.dataset.bound === '1') return;
            el.dataset.bound = '1';
            el.addEventListener('click', () => this.triggerExport(mode));
        };
        bind('btnModeReport', 'report');
        bind('btnModeCanva', 'canva');
        bind('btnModeGlobal', 'global');

        if (this._isoBusBound) return;
        this._isoBusBound = true;
        this.bus.on(SovereignEvents.ISOC_RENDERED, (data) => {
            const all = Array.isArray(data?.allPolygons) && data.allPolygons.length
                ? data.allPolygons
                : (data?.polygon ? [data.polygon] : []);
            this.activeIsoPolygons = all;
            // 裁點用最外圈（value 最大）；取景用全部圈並集
            this.activeIsoPolygon = all[0] || data?.polygon || null;
        });
        this.bus.on(SovereignEvents.ISOC_CLEARED, () => {
            this.activeIsoPolygon = null;
            this.activeIsoPolygons = [];
        });
    }

    onMapReady(map) {
        this.map = map;
    }

    setStatus(text) {
        const el = this.shadowRoot?.getElementById('status-text');
        if (el) el.textContent = text;
    }

    /** 全部圈的並集 bounds（禁止只跟目前視窗／單一內圈） */
    getIsoBounds() {
        this.hydrateIsoFromCabinet();
        const Lref = typeof window !== 'undefined' ? window.L : null;
        if (!Lref) return null;
        const polys = this.activeIsoPolygons?.length
            ? this.activeIsoPolygons
            : (this.activeIsoPolygon ? [this.activeIsoPolygon] : []);
        if (!polys.length) return null;
        try {
            const layer = Lref.geoJSON({
                type: 'FeatureCollection',
                features: polys
            });
            const bounds = layer.getBounds();
            return bounds.isValid() ? bounds : null;
        } catch {
            return null;
        }
    }

    /** 裁 POI：最外圈多邊形（涵蓋所有內圈） */
    getIsoClipPolygon() {
        this.hydrateIsoFromCabinet();
        if (this.activeIsoPolygons?.length) return this.activeIsoPolygons[0];
        return this.activeIsoPolygon;
    }

    /** 若总线事件早於截圖柜掛載，從分時柜回填全部圈 */
    hydrateIsoFromCabinet() {
        if (this.activeIsoPolygons?.length) return;
        const isoCab = this.getIsochroneCabinet();
        if (!isoCab || typeof isoCab.getActivePolygons !== 'function') return;
        try {
            const polys = isoCab.getActivePolygons() || [];
            if (!polys.length) return;
            this.activeIsoPolygons = polys;
            this.activeIsoPolygon = polys[0];
        } catch {
            /* ignore */
        }
    }

    getIsochroneCabinet() {
        return window.appCore?.getCabinet?.('isochrone') || null;
    }

    async triggerExport(mode) {
        if (!this.map) {
            this.setStatus('❌ 地圖尚未就緒');
            return;
        }
        if (this._exporting) {
            this.setStatus('⏳ 上一張截圖仍在合成');
            return;
        }

        const title = this.shadowRoot.getElementById('screenshotTitle').value
            || 'Sovereign GIS Analysis';
        this._exporting = true;
        const modeLabel = mode === 'report' ? '公車報表'
            : mode === 'canva' ? '分時人口報表'
            : mode === 'global' ? '生活機能圖' : mode;
        this.setStatus(`${modeLabel} · ghost 合成中…`);

        const hostEl = document.getElementById('sovereign-host');
        this.bus.emit(SovereignEvents.SCREENSHOT_START, { mode });
        if (hostEl) hostEl.classList.add('screenshot-clean-mode');

        try {
            const isoCab = this.getIsochroneCabinet();
            const polys = isoCab?.getActivePolygons?.() || [];

            if (mode === 'report') {
                if (polys.length && typeof isoCab.exportSnapshot === 'function') {
                    await isoCab.exportSnapshot();
                    this.setStatus('✅ 公車報表：路線圖 + 清單');
                    return;
                }
                this.bus.emit(SovereignEvents.SCREENSHOT_MODE_CHANGE, { fillOpacity: 0 });
                await new Promise((r) => setTimeout(r, 80));
            }

            // 分時人口雙圖：地圖依分時圈動態尺寸＋淺色交通底圖；人口表固定比例
            if (mode === 'canva') {
                if (polys.length && typeof isoCab.exportPopulationReport === 'function') {
                    // 不傳 captureMap：走專用 ghost 地圖（live 複製易失敗只剩人口表）
                    await isoCab.exportPopulationReport({});
                    return;
                }
                this.setStatus('⚠️ 分時人口報表需先有分時圈');
                return;
            }

            const bounds = this.getIsoBounds();
            // 生活機能圖：直接用活地圖已載入圖磚／圖示（含 POI、YouBike、停車場等），禁止重砌 ghost＋重抓
            const canvas = await this.captureGhost(
                mode,
                title,
                bounds,
                mode === 'global'
                    ? {
                        tileUrl: LIGHT_CARTO_TILE_URL,
                        injectPoiFromCabinet: false,
                        useLiveCache: true,
                        htmlMarkerCap: 8000
                    }
                    : {}
            );
            const prefix = mode === 'global' ? '生活機能圖' : `Sovereign_${mode}`;
            downloadCanvasPng(canvas, `${prefix}_${Date.now()}.png`);
            this.setStatus(mode === 'global'
                ? (bounds?.isValid?.()
                    ? `✅ 生活機能圖已導出（全機能・${this.activeIsoPolygons?.length || 1} 圈取景・17.9×12.1）`
                    : '✅ 生活機能圖已導出（全機能・目前畫面・17.9×12.1）')
                : '✅ 已用 ghost 導出（活地圖尺寸未改）');
        } catch (err) {
            this.setStatus(`❌ 失敗: ${err.message}`);
            console.error('[Screenshot] Error:', err);
        } finally {
            if (mode === 'report') {
                this.bus.emit(SovereignEvents.SCREENSHOT_MODE_CHANGE, { fillOpacity: 'restore' });
            }
            if (hostEl) hostEl.classList.remove('screenshot-clean-mode');
            this.bus.emit(SovereignEvents.SCREENSHOT_END, {});
            this._exporting = false;
        }
    }

    async captureGhost(mode, title, isoBounds, extra = {}) {
        const liveW = this.map.getContainer()?.clientWidth || 800;
        const liveH = this.map.getContainer()?.clientHeight || 600;
        let width = Math.round(liveW * (300 / 96));
        let height = Math.round(liveH * (300 / 96));
        let bounds = this.map.getBounds();
        let zoom = this.map.getZoom();
        let center = this.map.getCenter();
        let titleText = null;
        let polygonFillOpacity;

        if (mode === 'canva') {
            // 舊 Canva 單張尺寸保留給備援
            ({ width, height } = formatPixelSize(SCREENSHOT_FORMATS.CANVA_PX));
            titleText = title;
            if (isoBounds?.isValid?.()) bounds = isoBounds;
        } else if (mode === 'global') {
            // 紙本固定 17.9×12.1 cm；有圈必須以全圈並集為基準（fitBounds＋裁切；禁止鎖活圖視窗／整屏 letterbox）
            ({ width, height } = formatPixelSize(SCREENSHOT_FORMATS.GLOBAL_CM, SCREENSHOT_LAYOUT_DPI));
            if (isoBounds?.isValid?.()) {
                bounds = isoBounds;
                center = isoBounds.getCenter();
            } else {
                bounds = this.map.getBounds();
                center = this.map.getCenter();
                zoom = this.map.getZoom();
            }
        } else if (mode === 'population' && isoBounds?.isValid?.()) {
            // 舊做法：地圖大小依分時圈 bounds；DOM 用 96dpi，輸出再 × PRINT_SCALE
            const viewport = computeIsoDynamicViewport(isoBounds, this.map);
            if (viewport.valid) {
                width = viewport.width;
                height = viewport.height;
                zoom = viewport.zoom;
                center = isoBounds.getCenter();
            }
            bounds = isoBounds;
            polygonFillOpacity = 0;
        } else if (isoBounds?.isValid?.()) {
            const viewport = computeIsoDynamicViewport(isoBounds, this.map);
            if (viewport.valid) {
                width = viewport.width;
                height = viewport.height;
                zoom = viewport.zoom;
                center = isoBounds.getCenter();
            }
            bounds = isoBounds;
            polygonFillOpacity = 0;
        }

        const usePrintScale = mode === 'population' || mode === 'report' || mode === 'global'
            || (isoBounds?.isValid?.() && mode !== 'canva');
        const dpr = usePrintScale ? SCREENSHOT_PRINT_SCALE : 1;

        // POI圖：活地圖快取直出（圖磚／圖示已在 DOM）
        if (mode === 'global' && extra.useLiveCache !== false) {
            return captureLiveMapPrint(this.map, {
                width,
                height,
                bounds: isoBounds?.isValid?.() ? isoBounds : null,
                padding: [24, 24],
                maxZoom: isoBounds?.isValid?.() ? 17 : undefined,
                includeHtmlMarkers: true,
                devicePixelRatio: dpr,
                htmlMarkerCap: extra.htmlMarkerCap || 8000
            });
        }

        return captureGhostMapFromLive(this.map, {
            width,
            height,
            bounds,
            center,
            zoom,
            title: titleText,
            polygonFillOpacity,
            solidPolygons: mode === 'population',
            padding: mode === 'canva' ? [80, 80] : [24, 24],
            includeHtmlMarkers: true,
            devicePixelRatio: dpr,
            tileUrl: extra.tileUrl,
            injectPoiFromCabinet: extra.injectPoiFromCabinet === true,
            htmlMarkerCap: extra.htmlMarkerCap,
            maxZoom: mode === 'global' && isoBounds?.isValid?.() ? 17 : undefined,
            clipPolygon: mode === 'global' ? this.getIsoClipPolygon() : null
        });
    }
}

customElements.define('screenshot-cabinet', ScreenshotCabinet);

function dock() {
    if (window.appCore) {
        const instance = document.createElement('screenshot-cabinet');
        const container = document.getElementById('socket-screenshot');
        if (container) {
            container.innerHTML = '';
            container.appendChild(instance);
            window.appCore.registerCabinet('screenshot', instance);
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', dock);
} else {
    dock();
}
